import { useState, useMemo } from "react";
import { useInfiniteScroll } from "@/lib/api";
import {
  finiteNumber,
  formatOperatorReason,
  formatUtcTime,
  formatIndianTime,
  formatIndianDate,
} from "../lib/operatorFormatters";
import InfiniteListFooter from "./InfiniteListFooter";
import { History, TrendingUp, TrendingDown, Search, Award } from "lucide-react";

const fmt = (value) => {
  const number = finiteNumber(value);
  return number == null ? "—" : Number(number.toPrecision(6));
};

const signedCurrency = (value) => {
  const number = finiteNumber(value);
  if (number == null) return "—";
  const sign = number > 0 ? "+" : number < 0 ? "-" : "";
  return `${sign}$${Math.abs(number).toFixed(2)}`;
};

const signedPercent = (value) => {
  const number = finiteNumber(value);
  if (number == null) return "—";
  const sign = number > 0 ? "+" : "";
  return `${sign}${number.toFixed(2)}%`;
};

const realizedPnl = (trade) => {
  const pnl = Number(trade?.pnl ?? 0);
  return Number.isFinite(pnl) ? pnl : 0;
};

export const summarizeTrades = (trades = []) => {
  const list = Array.isArray(trades) ? trades : [];
  let totalPnl = 0;
  let wins = 0;

  list.forEach((trade) => {
    const pnl = realizedPnl(trade);
    totalPnl += pnl;
    if (pnl > 0) wins += 1;
  });

  const losses = list.length - wins;
  const winRate = list.length ? ((wins / list.length) * 100).toFixed(1) : "0.0";
  return { totalPnl, wins, losses, winRate };
};

export default function TradesTable({ trades: rawTrades = [], listState = {} }) {
  const trades = useMemo(() => {
    if (Array.isArray(rawTrades)) return rawTrades;
    if (Array.isArray(rawTrades?.data)) return rawTrades.data;
    if (Array.isArray(rawTrades?.items)) return rawTrades.items;
    return [];
  }, [rawTrades]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all, win, loss

  const handleScroll = useInfiniteScroll(listState.loadMore || (() => {}), listState.hasMore);

  const stats = useMemo(() => summarizeTrades(trades), [trades]);

  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      const sym = (t.symbol || "").toLowerCase();
      const matchSearch = !search || sym.includes(search.toLowerCase().trim());
      if (!matchSearch) return false;

      if (filter === "win") return realizedPnl(t) > 0;
      if (filter === "loss") return realizedPnl(t) <= 0;
      return true;
    });
  }, [trades, search, filter]);

  return (
    <div className="bg-zinc-950 border border-zinc-800/80 rounded-xl shadow-2xl flex flex-col w-full" data-testid="trades-panel">
      {/* Header Bar */}
      <div className="px-4 py-3 border-b border-zinc-800/70 flex flex-wrap items-center justify-between gap-3 bg-zinc-900/40 rounded-t-xl">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
            <History size={15} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-100 font-mono">
                Trade History ({trades?.length || 0})
              </h3>
              <span className="px-2 py-0.5 text-[10px] font-mono font-semibold rounded bg-zinc-900 text-zinc-300 border border-zinc-800 flex items-center gap-1">
                <Award size={11} className="text-emerald-400" /> Win Rate: {stats.winRate}%
              </span>
            </div>
            <p className="text-[10px] text-zinc-500 font-mono font-normal">Closed position performance & exit execution log</p>
          </div>
        </div>

        {/* Filter Tabs & Search */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Filter symbol..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-md pl-7 pr-2.5 py-1 text-[11px] font-mono text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/60 w-32"
            />
          </div>
          <div className="flex items-center gap-1 text-[10px] font-mono">
            {[
              { id: "all", label: `All (${trades.length})` },
              { id: "win", label: `Wins (${stats.wins})` },
              { id: "loss", label: `Losses (${stats.losses})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setFilter(tab.id)}
                className={`px-2 py-1 rounded-md transition-colors ${
                  filter === tab.id
                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 font-bold"
                    : "bg-zinc-900/60 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200 border border-zinc-800/40"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="infinite-scroll-container flex-1 overflow-x-auto overflow-y-auto max-h-[400px]" onScroll={handleScroll}>
        <table className="w-full text-xs font-mono border-collapse min-w-[850px]">
          <thead className="sticky top-0 z-20 bg-zinc-950/98 backdrop-blur-md text-zinc-400 border-b border-zinc-800 shadow-md">
            <tr className="text-left text-[10px] uppercase tracking-wider">
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-left bg-zinc-950">Closed At</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-left bg-zinc-950">Symbol</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Side</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Entry</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Exit</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Net Realized PnL</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-right bg-zinc-950">Gross Return %</th>
              <th scope="col" className="py-2.5 px-2 font-semibold text-zinc-400 text-center bg-zinc-950">Exit Reason</th>
              <th scope="col" className="py-2.5 px-3 font-semibold text-zinc-400 text-right bg-zinc-950">Mode</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900/80">
            {filteredTrades.length === 0 && (
              <tr>
                <td colSpan={9} className="py-10 text-center text-zinc-600" data-testid="no-trades">
                  No closed trades recorded
                </td>
              </tr>
            )}
            {filteredTrades.map((t) => {
              const pnl = realizedPnl(t);
              const isProfit = pnl > 0;
              const isLoss = pnl < 0;
              const pnlColor = isProfit
                ? "text-emerald-400"
                : isLoss
                ? "text-rose-400"
                : "text-zinc-400";
              const isLong = t.side === "long";
              const cleanSym = (t.symbol || "").replace(":USDT", "").replace("/USDT", "");
              const exitReason = formatOperatorReason(
                t.exit_reason_label,
                t.exit_reason,
                "Reason unavailable",
              );
              const exitReasonCode = String(
                t.exit_reason_code || t.exit_reason || "",
              ).toLowerCase();
              const grossPnl = finiteNumber(t.gross_pnl);
              const fees = finiteNumber(t.fees);
              const fundingFee = finiteNumber(t.funding_fee);
              const hasPnlBreakdown = Boolean(
                t.pnl_breakdown_available
                && grossPnl != null
                && fees != null
                && fundingFee != null,
              );

              return (
                <tr key={t.id} className="hover:bg-zinc-900/60 transition-colors align-middle" data-testid={`trade-row-${t.id}`}>
                  <td className="py-2.5 px-3 text-[11px] whitespace-nowrap" title={`Exchange UTC: ${formatUtcTime(t.closed_at)}`}>
                    <div className="font-semibold text-amber-300/90">{formatIndianTime(t.closed_at)}</div>
                    <div className="text-[9px] text-zinc-500">{formatIndianDate(t.closed_at, { includeYear: false })}</div>
                  </td>
                  <td className="py-2.5 px-2 font-bold text-zinc-100 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span>{cleanSym}</span>
                      <span className="text-[9px] text-zinc-500 bg-zinc-900 px-1 py-0.2 rounded border border-zinc-800">USDT</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-2 text-center whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      isLong ? "bg-emerald-950/80 text-emerald-400 border border-emerald-800/60" : "bg-rose-950/80 text-rose-400 border border-rose-800/60"
                    }`}>
                      {isLong ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {t.side}
                    </span>
                  </td>
                  <td className="py-2.5 px-2 text-right text-zinc-300 font-mono whitespace-nowrap">{fmt(t.entry_price)}</td>
                  <td className="py-2.5 px-2 text-right text-zinc-300 font-mono whitespace-nowrap">{fmt(t.exit_price)}</td>
                  <td className={`py-2.5 px-2 text-right font-bold whitespace-nowrap ${pnlColor}`}>
                    <div>{signedCurrency(pnl)}</div>
                    {hasPnlBreakdown ? (
                      <div
                        className="text-[9px] font-normal text-zinc-500"
                        title={`Gross ${signedCurrency(grossPnl)}; trading fees ${signedCurrency(t.trading_fees)}; funding ${signedCurrency(fundingFee)}`}
                      >
                        gross {signedCurrency(grossPnl)} · fees {signedCurrency(fees)}
                      </div>
                    ) : (
                      <div
                        className="text-[9px] font-normal text-zinc-600"
                        title="This legacy record has a net realized PnL but no verified fee breakdown."
                      >
                        net only
                      </div>
                    )}
                  </td>
                  <td className={`py-2.5 px-2 text-right font-bold whitespace-nowrap ${pnlColor}`}>
                    {signedPercent(t.gross_return_pct ?? t.pnl_pct)}
                  </td>
                  <td className="py-2.5 px-2 text-center whitespace-nowrap">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      exitReasonCode.includes("profit")
                        ? "bg-emerald-950/60 text-emerald-400 border border-emerald-800/40"
                        : exitReasonCode.includes("stop")
                        ? "bg-rose-950/60 text-rose-400 border border-rose-800/40"
                        : "bg-zinc-900 text-zinc-400 border border-zinc-800"
                    }`} title={t.exit_reason || undefined}>
                      {exitReason}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-right text-zinc-500 uppercase text-[10px] font-semibold whitespace-nowrap">
                    {t.mode || "LIVE"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!!trades?.length && (
          <InfiniteListFooter
            loadingMore={listState.loadingMore}
            hasMore={listState.hasMore}
            error={listState.error}
          />
        )}
      </div>
    </div>
  );
}
